import axios from "axios";
import { isLocalBackend } from "Routes/Routes";

// Production: https://prod-api-pdf.webnyay.in/
// Development: https://dev-api-pdf.webnyay.in/
export const DOC_MGMT_BASE_URL = "https://prod-api-pdf.webnyay.in/";

// Configurable password for Document Management API authentication
export const DOC_MGMT_PASSWORD = "Test@123";

// Maximum size of a single uploaded file (1 GB)
export const MAX_UPLOAD_SIZE_BYTES = 1024 * 1024 * 1024;
export const MAX_UPLOAD_SIZE_LABEL = "1 GB";

export const docMgmtInstance = axios.create({
  baseURL: DOC_MGMT_BASE_URL,
  timeout: 600000, // 10 minutes timeout for larger uploads/downloads
});

export const DOC_MGMT_TOKEN_KEY = "docMgmtToken";

export const DOC_MGMT_EMAIL_KEY = "docMgmtEmail";

export function getDocMgmtToken() {
  const stored = localStorage.getItem(DOC_MGMT_TOKEN_KEY);
  if (stored) return stored;

  const legacy =
    sessionStorage.getItem(DOC_MGMT_TOKEN_KEY) ||
    document.cookie.match(/(?:^|;\s*)pdfToken=([^;]*)/)?.[1];
  if (legacy) {
    localStorage.setItem(DOC_MGMT_TOKEN_KEY, legacy);
    return legacy;
  }
  return null;
}

function getDocMgmtEmail() {
  return localStorage.getItem(DOC_MGMT_EMAIL_KEY);
}

// Interceptor to inject the JWT Bearer token
docMgmtInstance.interceptors.request.use((config) => {
  const token = getDocMgmtToken();
  if (token) {
    config.headers.Authorization = "Bearer " + token;
  }
  return config;
});

// on 401 and replay the request. Concurrent 401s share a single token-auth call.
let authInFlight = null;

function renewDocMgmtToken(email) {
  if (!authInFlight) {
    authInFlight = authenticateDocMgmt(email).finally(() => {
      authInFlight = null;
    });
  }
  return authInFlight;
}

docMgmtInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config;
    const email = getDocMgmtEmail();

    if (error?.response?.status !== 401 || !config || config._docMgmtRetried || !email) {
      return Promise.reject(error);
    }

    config._docMgmtRetried = true;
    try {
      const token = await renewDocMgmtToken(email);
      if (!token) return Promise.reject(error);
      config.headers = { ...config.headers, Authorization: "Bearer " + token };
      return await docMgmtInstance(config);
    } catch (renewError) {
      console.error("Document Management token renewal failed:", renewError);
      return Promise.reject(error);
    }
  }
);

// Checks if the user has an active token stored
export function isDocMgmtAuthenticated() {
  return !!getDocMgmtToken();
}

// Clears the stored token (and its legacy sessionStorage / cookie copies)
export function logoutDocMgmt() {
  localStorage.removeItem(DOC_MGMT_TOKEN_KEY);
  localStorage.removeItem(DOC_MGMT_EMAIL_KEY);
  sessionStorage.removeItem(DOC_MGMT_TOKEN_KEY);
  document.cookie = "pdfToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
}

// Calls /token-auth/ and stores the token for every tab of this origin
export async function authenticateDocMgmt(email) {
  try {
    const response = await axios.post(`${DOC_MGMT_BASE_URL}token-auth/`, {
      email: email,
      password: DOC_MGMT_PASSWORD,
    });
    const token = response.data.token;
    if (token) {
      localStorage.setItem(DOC_MGMT_TOKEN_KEY, token);
      localStorage.setItem(DOC_MGMT_EMAIL_KEY, email);
      document.cookie = `pdfToken=${token}; path=/;`;
      return token;
    }
  } catch (error) {
    console.error("Document Management authentication failed:", error);
    throw error;
  }
}

// 5.1 Get Files in a Folder
export async function getDocMgmtFiles(parentFolderId, page = 1, pageSize = 1000, config = {}) {
  const folderIdParam = (parentFolderId === 0 || parentFolderId === "0") ? null : parentFolderId;
  return await docMgmtInstance.get(`get-files/${folderIdParam}/`, {
    ...config,
    params: { page, page_size: pageSize, ...config.params },
  });
}

// 5.2 Get Folders in a Parent Folder
export async function getDocMgmtFolders(parentFolderId, page = 1, pageSize = 1000, config = {}) {
  const folderIdParam = (parentFolderId === 0 || parentFolderId === "0") ? null : parentFolderId;
  return await docMgmtInstance.get(`get-folders/${folderIdParam}/`, {
    ...config,
    params: { page, page_size: pageSize, ...config.params },
  });
}

// 5.3 Get Folder by ID
export async function getDocMgmtFolderById(folderId, config = {}) {
  return await docMgmtInstance.get(`get-folder-by-id/${folderId}/`, config);
}

// 5.5 Upload a File
export async function uploadDocMgmtFile(file, parentFolderId, config = {}, webnyayOwner) {
  const data = new FormData();
  data.append("file", file);
  data.append("name", file.name);
  data.append("path", "sd");
  data.append("type", file.type === "" ? "text/plain" : file.type);
  data.append("size", file.size);
  if (parentFolderId && parentFolderId !== 0 && parentFolderId !== "0") {
    data.append("parent_folder", parentFolderId);
  }
  if (webnyayOwner) {
    data.append("webnyay_owner", webnyayOwner);
  }
  return await docMgmtInstance.post("upload-file/", data, {
    ...config,
    headers: {
      "Content-Type": "multipart/form-data",
      ...config.headers,
    },
  });
}

// 5.6 Create a Folder
export async function createDocMgmtFolder(name, parentFolderId, config = {}, webnyayOwner) {
  const body = {
    name: name,
  };
  if (parentFolderId && parentFolderId !== 0 && parentFolderId !== "0") {
    body.parent_folder = parentFolderId;
  }
  if (webnyayOwner) {
    body.webnyay_owner = webnyayOwner;
  }
  return await docMgmtInstance.post("create-folder/", body, config);
}

// 5.7 Rename / Update a File
export async function renameDocMgmtFile(fileId, name, config = {}) {
  return await docMgmtInstance.put(`file-delete-update/${fileId}/`, { name }, config);
}

// 5.8 Delete a File
export async function deleteDocMgmtFile(fileId, config = {}) {
  return await docMgmtInstance.delete(`file-delete-update/${fileId}/`, config);
}

// 5.9 Rename / Update a Folder
export async function renameDocMgmtFolder(folderId, nameToShowUser, config = {}) {
  return await docMgmtInstance.put(`folder-delete-update/${folderId}/`, { nameToShowUser }, config);
}

// 5.10 Delete a Folder
export async function deleteDocMgmtFolder(folderId, config = {}) {
  return await docMgmtInstance.delete(`folder-delete-update/${folderId}/`, config);
}

// 6.1 Download a Single File (Get Presigned URL)
export async function downloadDocMgmtFile(fileId, config = {}) {
  return await docMgmtInstance.get(`file-presigned-url/${fileId}/`, config);
}

// 6.1b Get Presigned URL for File Path
export async function getDocMgmtFilePresignedUrlForPath(workspaceId, path, config = {}) {
  return await docMgmtInstance.get(`file-presiged-url-for-path/${workspaceId}/`, {
    ...config,
    params: { path, ...config.params },
  });
}

// 6.2 Download Selected Files (Bulk Download as ZIP)
export async function downloadDocMgmtSelectedFiles(fileIds, folderIds, workspaceId, config = {}) {
  console.log('inside main function', workspaceId)
  return await docMgmtInstance.post(
    `download/`,
    {
      file_ids: fileIds.map((id) => parseInt(id)),
      folder_ids: folderIds.map((id) => parseInt(id)),
      workspace_id: workspaceId,
    },
    {
      ...config,
      responseType: "blob",
    }
  );
}

// Get individual workspace details
export async function getDocMgmtWorkspace(workspaceId, config = {}) {
  return await docMgmtInstance.get(`api/workspace/${workspaceId}/`, config);
}

// 7.1 Paginate Workspace
export async function paginateWorkspace(workspaceId, paginationType, folderId, config = {}) {
  return await docMgmtInstance.get(`paginate-workspace/${workspaceId}/${paginationType}/${folderId}/`, config);
}

// 7.2 Get Pagination Progress
export async function getPaginationProgress(workspaceId, folderId, config = {}) {
  return await docMgmtInstance.get(`get-number-of-paginated-files/${workspaceId}/${folderId}/`, config);
}

// 7.3 Get OCRable files list
export async function getOcrableFilesList(workspaceId, folderId, config = {}) {
  return await docMgmtInstance.get(`get-ocrable-files-list/${workspaceId}/${folderId}/`, config);
}

// 7.4 Trigger OCR processing
export async function ocrFilesForWorkspace(workspaceId, folderId, config = {}) {
  return await docMgmtInstance.get(`ocr-files-for-workspace/${workspaceId}/${folderId}/`, config);
}

// Search by file name
export async function findSearchByFileName(query, startPage, perPage, workspaceId, config = {}) {
  const cleanQuery = query.replace(/[^\w\s]/gi, "");
  return await docMgmtInstance.get(`find-by-file-name/${workspaceId}/${cleanQuery}/${startPage}/${perPage}/`, config);
}

// Search by page number
export async function findSearchByPagination(pageNumber, workspaceId, config = {}) {
  const cleanPage = pageNumber.replace(/[^\w\s]/gi, "-");
  return await docMgmtInstance.get(`find-by-pagination/${workspaceId}/${cleanPage}/`, config);
}

// Search inside file content
export async function findSearchInFiles(query, startPage, perPage, workspaceId, config = {}) {
  const cleanQuery = query.replace(/[^\w\s]/gi, "");
  return await docMgmtInstance.get(`search-in-files/${workspaceId}/${cleanQuery}/${startPage}/${perPage}/`, config);
}

// Bookmark Folder / Merge Documents
export async function bookmarkFolder(folderId, config = {}) {
  return await docMgmtInstance.get(`generate-bookmarked-index-file/${folderId}/`, config);
}

// Generate complete bundle index file
export async function generateIndexFile(workspaceId, folderId, config = {}) {
  return await docMgmtInstance.get(`generate-index-file/${workspaceId}/${folderId}`, config);
}

